/*
 * Global state singletons — module-level mutable state consumed by both
 * React components (via contexts) and non-component modules (API client,
 * fetcher engine, S3 client). Mirrors the pattern from the original
 * layoutcontext.js where refs are exported as module-level singletons.
 *
 * These are intentionally "unsafe" mutable globals — they are the shared
 * bus that the backend-driven dashboard engine relies on.
 */

import type { AxiosHeaders } from "axios";

/* i18n dictionary — fetched via get_tokens(), keyed by lowercased token */
export const dictionary: Record<string, unknown> & { isLoaded?: boolean } = {
    isLoaded: false,
};

/* Static data cache — fetcher `entry` results + enable_cache + edit state */
export const staticData: Record<string, unknown> = {};

/* Refs — DOM/component refs accessible outside React */
export const refs: Record<string, unknown> = {};

/* Active context singleton — mirrors activeCtx from layoutcontext.js */
export const activeCtx: {
    ctx?: string[];
    properties?: Record<string, unknown>;
    region?: string;
    refresh?: boolean;
} = { ctx: [], properties: {}, refresh: false };

/* Data context singleton — mirrors dataCtx from layoutcontext.js */
export const dataCtx: { path?: unknown; lang: string } = { path: {}, lang: "EN-US" };

/* Form state — field values keyed by entry name */
export const formData: Record<string, unknown> = {};

/* Fetched data — keyed by response index */
export const fetchData: Record<string, unknown> = {};

/* API responses — keyed by response index */
export const responses: Record<string, unknown> = {};

/* Persistent filters */
export const filtersPersistent: Record<string, unknown> = {};

/* Actions registry */
export const actions: Record<string, unknown> = {};

/* S3 active credentials cache — keyed by project_id */
export const s3_active_credentials: Record<string, unknown> = {};

/* User ref — mirrors user_ref; holds current user state outside React */
export const user_ref: { user: { permissions: string[]; role?: string; theme?: string; lang?: string; name?: string } } = {
    user: { permissions: [] },
};

/* Promise registry for wait_for / resolve_entry */
export const promises: Record<string, { promise: Promise<unknown>; resolve: (value?: unknown) => void; reject: (reason?: unknown) => void }> = {};

/* Valid entries list (for form validation) */
export const validEntries: string[] = [];

/* Validation notifiers */
export const validationNotifiers: Record<string, Record<string, { callback: (valid: boolean) => void; entries: string[] }>> = {};

/* Form notification registry */
export const formNotification: Record<string, Record<string, (updater: (s: unknown) => unknown) => void>> = {};
export const hideNotification: Record<string, Record<string, (updater: (s: unknown) => unknown) => void>> = {};

/* Refresh data notifiers */
export const refreshDataNotifiers: Record<string, () => void> = {};

/* Notifiers registry */
export const notifiers: Record<string, Record<string, Record<string, (params?: unknown) => void>>> = {};

/* Modals to close registry */
const modalsToClose: Record<string, () => void> = {};

export const registerModal = (modal_id: string, setVisible: () => void) => {
    modalsToClose[modal_id] = setVisible;
};

export const closeModals = () => {
    for (const k of Object.values(modalsToClose)) {
        k();
    }
};

/* wait_for / resolve_entry — promise-based entry synchronization */
export const wait_for = (entry: string): Promise<unknown> => {
    if (!promises[entry]) {
        const prom: { promise: Promise<unknown>; resolve: (value?: unknown) => void; reject: (reason?: unknown) => void } = {
            promise: undefined as unknown as Promise<unknown>,
            resolve: () => {},
            reject: () => {},
        };
        prom.promise = new Promise((resolve, reject) => {
            prom.resolve = resolve;
            prom.reject = reject;
        });
        promises[entry] = prom;
    }
    return promises[entry].promise;
};

export const resolve_entry = (entry: string, value?: unknown) => {
    if (!promises[entry]) {
        const prom: { promise: Promise<unknown>; resolve: (value?: unknown) => void; reject: (reason?: unknown) => void } = {
            promise: undefined as unknown as Promise<unknown>,
            resolve: () => {},
            reject: () => {},
        };
        prom.promise = new Promise((resolve, reject) => {
            prom.resolve = resolve;
            prom.reject = reject;
        });
        promises[entry] = prom;
    }
    promises[entry].resolve(value);
};

/* Validation helpers */
export const allValid = (entries: string[]): boolean => {
    for (const e of entries) {
        if (validEntries.indexOf(e) === -1) {
            return false;
        }
    }
    return true;
};

const notifyValidations = (entry: string) => {
    for (const notifier of Object.values(validationNotifiers[entry] || {})) {
        notifier.callback(allValid(notifier.entries));
    }
};

export const listenValidations = (idx: string, entries: string[], callback: (valid: boolean) => void) => {
    for (const entry of entries) {
        let validators = validationNotifiers[entry];
        if (!validators) {
            validators = {};
            validationNotifiers[entry] = validators;
        }
        validators[idx] = { callback, entries };
        notifyValidations(entry);
    }
};

export const validateEntry = (entry: string) => {
    if (validEntries.indexOf(entry) === -1) {
        validEntries.push(entry);
        notifyValidations(entry);
    }
};

export const invalidateEntry = (entry: string) => {
    const idx = validEntries.indexOf(entry);
    if (idx !== -1) {
        validEntries.splice(idx, 1);
        notifyValidations(entry);
    }
};

/* Notifier helpers */
export const add_notifiers = (idx: string, key: string, notifier: unknown) => {
    notifiers[`${idx}.${key}`] = notifiers[`${idx}.${key}`] || {};
    let dt = staticData[idx] as Record<string, unknown>;
    if (!dt) {
        dt = {};
        staticData[idx] = dt;
    }
    dt[key] = notifier;
};

export const listen_notifiers = (evaluator: string, key: string, callback: (params?: unknown) => void, idx: string) => {
    for (const k in notifiers) {
        if (evaluator.indexOf(k) >= 0) {
            let notif = notifiers[k][key];
            if (notif) {
                notif[idx] = callback;
            } else {
                notif = notifiers[k][key] = { [idx]: callback };
            }
        }
    }
};

export const notify = (idx: string, key: string, params?: unknown) => {
    Object.values(notifiers[`${idx}.${key}`] || {}).forEach((k) => {
        Object.values(k).forEach((y) => y(params));
    });
};

export const listRefreshData = (idx: string, callback: () => void) => {
    refreshDataNotifiers[idx] = callback;
};

export const notifyRefreshData = (notifierList: string[]) => {
    for (const notif of notifierList) {
        const refreshDataNotifier = refreshDataNotifiers[notif];
        if (refreshDataNotifier) {
            refreshDataNotifier();
        }
    }
};

/* Form notification helpers */
export const apply_form_notification = (state: { validations?: unknown[]; disable_filter?: string; idx?: string; entry?: string }, setState: (updater: (s: unknown) => unknown) => void, form = true) => {
    if (!form) {
        const entries = [...(state.disable_filter || "").matchAll(/formData(\.|\[(['"]))(\w+)((['"])])?/g)].map((x) => x[3]);
        for (const entry of entries) {
            const _entry = hideNotification[entry as string];
            if (_entry) {
                _entry[state.idx as string] = setState;
            } else {
                hideNotification[entry as string] = { [state.idx as string]: setState };
            }
        }
        return;
    }
    const validations = state.validations as { validation: string }[] | undefined;
    if (!validations || validations.length === 0) {
        return;
    }
    for (const validation of validations) {
        const _validation = validation.validation;
        const entries = [..._validation.matchAll(/formData(\.|\[(['"]))(\w+)((['"])])?/g)].map((x) => x[3]);
        for (const entry of entries) {
            const _entry = formNotification[entry as string];
            if (_entry) {
                _entry[state.entry as string] = setState;
            } else {
                formNotification[entry as string] = { [state.entry as string]: setState };
            }
        }
    }
};

export const notify_form = (state: { entry?: string }, form = true) => {
    const entry = state.entry || "";
    const to_notify = form ? formNotification[entry] : hideNotification[entry];
    if (!to_notify) {
        return;
    }
    for (const [, notify] of Object.entries(to_notify)) {
        (notify as (s: unknown) => void)((s: unknown) => ({ ...(s as Record<string, unknown>), refresh: !((s as { refresh?: boolean }).refresh) }));
    }
};
